import { sendToExtension, createPageMayExitObservable, startTelemetry, canUseEventBridge, addTelemetryDebug, startAccountContext, startGlobalContext, startUserContext } from '@datadog/browser-core';
import { createDOMMutationObservable } from '../browser/domMutationObservable';
import { createWindowOpenObservable } from '../browser/windowOpenObservable';
import { startInternalContext } from '../domain/contexts/internalContext';
import { LifeCycle } from '../domain/lifeCycle';
import { startViewHistory } from '../domain/contexts/viewHistory';
import { startRequestCollection } from '../domain/requestCollection';
import { startActionCollection } from '../domain/action/actionCollection';
import { startErrorCollection } from '../domain/error/errorCollection';
import { startResourceCollection } from '../domain/resource/resourceCollection';
import { startViewCollection } from '../domain/view/viewCollection';
import { startRumSessionManager, startRumSessionManagerStub } from '../domain/rumSessionManager';
import { startRumBatch } from '../transport/startRumBatch';
import { startRumEventBridge } from '../transport/startRumEventBridge';
import { startUrlContexts } from '../domain/contexts/urlContexts';
import { createLocationChangeObservable } from '../browser/locationChangeObservable';
import { startFeatureFlagContexts } from '../domain/contexts/featureFlagContext';
import { startCustomerDataTelemetry } from '../domain/startCustomerDataTelemetry';
import { startPageStateHistory } from '../domain/contexts/pageStateHistory';
import { startDisplayContext } from '../domain/contexts/displayContext';
import { startVitalCollection } from '../domain/vital/vitalCollection';
import { startCiVisibilityContext } from '../domain/contexts/ciVisibilityContext';
import { startLongAnimationFrameCollection } from '../domain/longAnimationFrame/longAnimationFrameCollection';
import { RumPerformanceEntryType, supportPerformanceTimingEvent } from '../browser/performanceObservable';
import { startLongTaskCollection } from '../domain/longTask/longTaskCollection';
import { startSyntheticsContext } from '../domain/contexts/syntheticsContext';
import { startRumAssembly } from '../domain/assembly';
import { startSessionContext } from '../domain/contexts/sessionContext';
import { startConnectivityContext } from '../domain/contexts/connectivityContext';
import { startDefaultContext } from '../domain/contexts/defaultContext';
import { startTrackingConsentContext } from '../domain/contexts/trackingConsentContext';
import { createHooks } from '../domain/hooks';
import { startEventCollection } from '../domain/event/eventCollection';
import { startInitialViewMetricsTelemetry } from '../domain/view/viewMetrics/startInitialViewMetricsTelemetry';
export function startRum(configuration, recorderApi, profilerApi, initialViewOptions, createEncoder, // `startRum` and its subcomponents assume tracking consent is granted initially and starts
// collecting logs unconditionally. As such, `startRum` should be called with a
// `trackingConsentState` set to "granted".
trackingConsentState, customVitalsState, bufferedDataObservable, sdkName) {
  var cleanupTasks = [];
  var lifeCycle = new LifeCycle();
  var hooks = createHooks();
  lifeCycle.subscribe(13
  /* LifeCycleEventType.RUM_EVENT_COLLECTED */
  , function (event) {
    return sendToExtension('rum', event);
  });

  var reportError = function reportError(error) {
    lifeCycle.notify(14
    /* LifeCycleEventType.RAW_ERROR_COLLECTED */
    , {
      error: error
    }); // monitor-until: forever, to keep an eye on the errors reported to customers

    addTelemetryDebug('Error reported to customer', {
      'error.message': error.message
    });
  };

  var pageMayExitObservable = createPageMayExitObservable(configuration);
  var pageMayExitSubscription = pageMayExitObservable.subscribe(function (event) {
    lifeCycle.notify(11
    /* LifeCycleEventType.PAGE_MAY_EXIT */
    , event);
  });
  cleanupTasks.push(function () {
    return pageMayExitSubscription.unsubscribe();
  });
  var telemetry = startTelemetry("browser-rum-sdk"
  /* TelemetryService.RUM */
  , configuration, hooks, reportError, pageMayExitObservable, createEncoder);
  cleanupTasks.push(telemetry.stop);
  var session = !canUseEventBridge() ? startRumSessionManager(configuration, lifeCycle, trackingConsentState) : startRumSessionManagerStub();

  if (!canUseEventBridge()) {
    var batch = startRumBatch(configuration, lifeCycle, reportError, pageMayExitObservable, session.expireObservable, createEncoder);
    cleanupTasks.push(function () {
      return batch.stop();
    });
    startCustomerDataTelemetry(telemetry, lifeCycle, batch.flushController.flushObservable);
  } else {
    startRumEventBridge(lifeCycle);
  }

  var domMutationObservable = createDOMMutationObservable();
  var locationChangeObservable = createLocationChangeObservable(configuration, location);

  var _createWindowOpenObse = createWindowOpenObservable(),
      windowOpenObservable = _createWindowOpenObse.observable,
      stopWindowOpen = _createWindowOpenObse.stop;

  cleanupTasks.push(stopWindowOpen);
  startDefaultContext(hooks, configuration, sdkName);
  var pageStateHistory = startPageStateHistory(hooks, configuration);
  var viewHistory = startViewHistory(lifeCycle);
  cleanupTasks.push(function () {
    return viewHistory.stop();
  });
  var urlContexts = startUrlContexts(lifeCycle, hooks, locationChangeObservable, location);
  cleanupTasks.push(function () {
    return urlContexts.stop();
  });
  var featureFlagContexts = startFeatureFlagContexts(lifeCycle, hooks, configuration);
  startSessionContext(hooks, session, recorderApi, viewHistory);
  startConnectivityContext(hooks);
  startTrackingConsentContext(hooks, trackingConsentState);
  var globalContext = startGlobalContext(hooks, configuration, 'rum', true);
  var userContext = startUserContext(hooks, configuration, session, 'rum');
  var accountContext = startAccountContext(hooks, configuration, 'rum');

  var _startRumEventCollect = startRumEventCollection(lifeCycle, hooks, configuration, pageStateHistory, domMutationObservable, windowOpenObservable, reportError),
      actionContexts = _startRumEventCollect.actionContexts,
      addAction = _startRumEventCollect.addAction,
      addEvent = _startRumEventCollect.addEvent,
      stopRumEventCollection = _startRumEventCollect.stop;

  cleanupTasks.push(stopRumEventCollection);

  var _startViewCollection = startViewCollection(lifeCycle, hooks, configuration, location, domMutationObservable, windowOpenObservable, locationChangeObservable, recorderApi, viewHistory, initialViewOptions),
      addTiming = _startViewCollection.addTiming,
      startView = _startViewCollection.startView,
      setViewName = _startViewCollection.setViewName,
      setViewContext = _startViewCollection.setViewContext,
      setViewContextProperty = _startViewCollection.setViewContextProperty,
      getViewContext = _startViewCollection.getViewContext,
      stopViewCollection = _startViewCollection.stop;

  cleanupTasks.push(stopViewCollection);

  var _startInitialViewMetr = startInitialViewMetricsTelemetry(lifeCycle, telemetry),
      stopInitialViewMetricsTelemetry = _startInitialViewMetr.stop;

  cleanupTasks.push(stopInitialViewMetricsTelemetry);

  var _startResourceCollect = startResourceCollection(lifeCycle, configuration, pageStateHistory),
      stopResourceCollection = _startResourceCollect.stop;

  cleanupTasks.push(stopResourceCollection);

  if (configuration.trackLongTasks) {
    if (supportPerformanceTimingEvent(RumPerformanceEntryType.LONG_ANIMATION_FRAME)) {
      var _startLongAnimationFr = startLongAnimationFrameCollection(lifeCycle, configuration),
          stopLongAnimationFrameCollection = _startLongAnimationFr.stop;

      cleanupTasks.push(stopLongAnimationFrameCollection);
    } else {
      startLongTaskCollection(lifeCycle, configuration);
    }
  }

  var _startErrorCollection = startErrorCollection(lifeCycle, configuration, bufferedDataObservable),
      addError = _startErrorCollection.addError;

  bufferedDataObservable.unbuffer();
  startRequestCollection(lifeCycle, configuration, session, userContext, accountContext);
  var vitalCollection = startVitalCollection(lifeCycle, pageStateHistory, customVitalsState);
  var internalContext = startInternalContext(configuration.applicationId, session, viewHistory, actionContexts, urlContexts); // Add Clean-up tasks for Profiler API.

  cleanupTasks.push(function () {
    return profilerApi.stop();
  });
  return {
    addAction: addAction,
    addEvent: addEvent,
    addError: addError,
    addTiming: addTiming,
    addFeatureFlagEvaluation: featureFlagContexts.addFeatureFlagEvaluation,
    startView: startView,
    setViewContext: setViewContext,
    setViewContextProperty: setViewContextProperty,
    getViewContext: getViewContext,
    setViewName: setViewName,
    lifeCycle: lifeCycle,
    viewHistory: viewHistory,
    session: session,
    stopSession: function stopSession() {
      return session.expire();
    },
    getInternalContext: internalContext.get,
    startDurationVital: vitalCollection.startDurationVital,
    stopDurationVital: vitalCollection.stopDurationVital,
    addDurationVital: vitalCollection.addDurationVital,
    addOperationStepVital: vitalCollection.addOperationStepVital,
    globalContext: globalContext,
    userContext: userContext,
    accountContext: accountContext,
    telemetry: telemetry,
    stop: function stop() {
      cleanupTasks.forEach(function (task) {
        return task();
      });
    },
    hooks: hooks
  };
}
export function startRumEventCollection(lifeCycle, hooks, configuration, pageStateHistory, domMutationObservable, windowOpenObservable, reportError) {
  var actionCollection = startActionCollection(lifeCycle, hooks, domMutationObservable, windowOpenObservable, configuration);
  var eventCollection = startEventCollection(lifeCycle);
  var displayContext = startDisplayContext(hooks, configuration);
  var ciVisibilityContext = startCiVisibilityContext(configuration, hooks);
  startSyntheticsContext(hooks);
  startRumAssembly(configuration, lifeCycle, hooks, reportError);
  return {
    pageStateHistory: pageStateHistory,
    addAction: actionCollection.addAction,
    addEvent: eventCollection.addEvent,
    actionContexts: actionCollection.actionContexts,
    stop: function stop() {
      actionCollection.stop();
      ciVisibilityContext.stop();
      displayContext.stop();
      pageStateHistory.stop();
    }
  };
}